import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { t } from 'i18next';
import { memo, useCallback, useContext, useMemo } from 'react';

import Checkbox from '~components/Checkbox/Checkbox';
import { BillingLine } from '~hooks/useBillingLines/models';

import { getSettleStatusBySide, isBillingLineSelectable } from '../chargeSelection';
import ChargesSelectionContext, {
  ChargesSelectionActionsContext,
} from '../ChargesSelectionContext';

interface ChargeLinesCheckboxProps {
  label: string;
  lines: BillingLine[];
}

export default function ChargeLinesCheckbox({ label, lines }: ChargeLinesCheckboxProps) {
  const { getLinesCheckedState } = useContext(ChargesSelectionContext);
  const { toggleLinesCheck } = useContext(ChargesSelectionActionsContext);

  const { hasSelectableLines, selectLinesLabel, tooltip } = useMemo(() => {
    const hasUnselectableLines = !lines.every(isBillingLineSelectable);
    const selectLabel = t('approvals.approve_charges.selection.select_lines', {
      interpolation: { escapeValue: false },
      name: label,
    });

    return {
      hasSelectableLines: lines.some(isBillingLineSelectable),
      selectLinesLabel: selectLabel,
      tooltip: hasUnselectableLines
        ? getSettleStatusBySide(lines).join(' · ')
        : selectLabel,
    };
  }, [label, lines]);

  const toggleLines = useCallback(
    () => toggleLinesCheck(lines),
    [lines, toggleLinesCheck],
  );

  if (lines.length === 0) {
    return null;
  }

  const checkedState = getLinesCheckedState(lines);

  return (
    <LinesCheckbox
      ariaLabel={selectLinesLabel}
      isChecked={checkedState === true}
      isDisabled={!hasSelectableLines}
      isIntermediate={checkedState === 'intermediate'}
      onChange={toggleLines}
      tooltip={tooltip}
    />
  );
}

interface LinesCheckboxProps {
  ariaLabel: string;
  isChecked: boolean;
  isDisabled: boolean;
  isIntermediate: boolean;
  onChange: () => void;
  tooltip: string;
}

const LinesCheckbox = memo(function LinesCheckbox({
  ariaLabel,
  isChecked,
  isDisabled,
  isIntermediate,
  onChange,
  tooltip,
}: LinesCheckboxProps) {
  return (
    <Tooltip disableInteractive title={tooltip}>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        <Checkbox
          ariaLabel={ariaLabel}
          isChecked={isChecked}
          isDisabled={isDisabled}
          isIntermediate={isIntermediate}
          onChange={onChange}
        />
      </Box>
    </Tooltip>
  );
});
